import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { io } from "socket.io-client";
import ReactLoading from "react-loading";
import { isChrome, isMobile } from "react-device-detect";
import { Modal } from "../../components/Modal";
import { FormModal } from "../../components/FormModal";
import { FloatingSelect } from "../../components/FloatingSelect";
import api from "../../services/api";
import { showToast } from "../../components/CustomToast";

import {
  Container,
  Header,
  ListContainer,
  PopupContentWrapper,
  Form,
  TagsContainer,
  FilterContainer,
  Tag,
  Footer,
  LoadMore,
  TotalInfo,
  TotalContainer,
  TotalMobileView,
  UrgencyBadge,
  StatusBolinha,
  HeaderActions,
  SearchContainer,
  SearchSelectWrapper,
  SearchSelectButton,
  SearchInput,
  SearchButton,
  SearchSuggestionsList,
  SearchSuggestionItem,
  ClearSearchButton,
  SelectedFilterBadge,
  SelectedFilterTag,
  ProductionQueueContainer,
  ProductionQueueWrapper,
  ProductionQueueTitle,
  ProductionQueueSection,
  UrgentPriorityBadge,
  BadgeWrapper,
  RightControlsWrapper,
  QueueCountBadge,
  EmptyQueueMessage,
} from "./styles";
import { i18n } from "../../localization/i18n";
import { useAuth } from "../../hooks/AuthContext";
import { Filter } from "./components/Filter";
import {
  IFileChoice,
  IFilter,
  IOrder,
  IPopupData,
  IPrintChoiceFormData,
  IPeriod,
} from "./dtos";
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, startOfDay, endOfDay } from "date-fns";
import PrintHistoryPopUpModal from "../../components/PrintHistoryPopUp";
import { getApiGetOrdersFunctionByRole } from "./service";
import { abridgedControl } from "../../utils/strings";
import { usePrinters } from "../../hooks/PrintersContext";
import PrintChoose from "./components/PrintChoose";
import DownloadFile from "./components/DownloadFile";
import PrintControl from "./components/PrintControl";

import { IParams } from "./dtos";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { orderSituation, orderStatus, orderUrgencyTypes, roles, solutionTypes } from "../../utils/constants";
import { PrintManagementPopUpModal } from "../../components/PrintManagementPopUp";
import PlanitUploadingPopUpModal from "./components/PlanitUploadingPopUpModal";
import { BsDownload } from "react-icons/bs";
import { OrdersToXlsFormatFactory } from "./factories/OrdersToXlsFormatFactory";
import { xlsxColumnsByUserRole } from "./mocks";

import { writeFile, utils } from "xlsx";
import CustomPrintControl from "./components/CustomPrintControl";
import Evaluation from "../licensees/pages/Evaluation";
import ShowOrder from "./ShowOrder";

import { Menu, MenuItem } from "@mui/material";
import { Table, Column, TableAction } from "../../components/Table";
import { DateDropdownFilter, DateFilterOption } from "../../components/DateDropdownFilter";
import { DropdownFilter } from "../../components/DropdownFilter";
import { PrimaryButton } from "../../components/PrimaryButton";
import { FiCalendar, FiEdit2, FiEye, FiPaperclip, FiCheck, FiX, FiGift, FiBox, FiChevronDown, FiChevronUp, FiChevronRight } from "react-icons/fi";
import { MdOutlineCancel } from "react-icons/md";
import { RiSearchLine, RiMoneyDollarCircleLine, RiAlarmWarningFill, RiCloseFill, RiFlashlightFill, RiPrinterLine, RiHistoryLine, RiFileListLine, RiRulerLine, RiToolsFill, RiHourglassFill } from "react-icons/ri";

import { CustomOrderUploadForm } from "./components/CustomOrderUploadForm";

export function Orders() {
  const { user, setNavigationAction, currency } = useAuth();
  const { closure } = useParams<keyof IParams>();
  const location = useLocation();
  const { defaultPrinter, userHasPrinter } = usePrinters();

  const getOrdersService = getApiGetOrdersFunctionByRole(user.role, user.person?.licensee?.printers?.length > 0);

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<"number" | "licensee" | "patient">("number");
  const canSelectSearchType = user && [
    roles.admin,
    roles.saf,
    roles.creator,
    roles.licensee,
    roles.employee,
    roles.fixiter
  ].includes(user.role);
  const canSearchByLicensee = user && [roles.admin, roles.saf, roles.creator].includes(user.role);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLicenseeId, setSelectedLicenseeId] = useState<string | null>(null);
  const [searchMenuAnchorEl, setSearchMenuAnchorEl] = useState<null | HTMLElement>(null);
  const openSearchMenu = Boolean(searchMenuAnchorEl);
  const [editingBillingOrder, setEditingBillingOrder] = useState<IOrder>();
  const [selectedBillingMethod, setSelectedBillingMethod] = useState("");
  const [isChangingBilling, setIsChangingBilling] = useState(false);
  const [uploadingCustomOrder, setUploadingCustomOrder] = useState<IOrder>();

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [productionQueueOrders, setProductionQueueOrders] = useState<IOrder[]>([]);
  const [loadingProductionQueue, setLoadingProductionQueue] = useState(false);
  const [isProductionQueueExpanded, setIsProductionQueueExpanded] = useState<boolean>(true);

  const toggleProductionQueue = useCallback(() => {
    setIsProductionQueueExpanded(prev => !prev);

    if (!isProductionQueueExpanded) {
      const now = new Date();
      setFilter({
        statuses: [],
        urgencies: [],
        billing_methods: [],
        solution_types: [],
        period: { from: startOfYear(now), to: endOfYear(now) },
        month: undefined,
      });
      setDateOption("this_year");
      setCustomPeriod(undefined);
      setSearchTerm("");
      setSearchQuery("");
      setSelectedLicenseeId(null);
      setSuggestions([]);
      setShowSuggestions(false);
      setPage(1);
    }
  }, [isProductionQueueExpanded]);
  const isRealTimeActive = useMemo(() => {
    return !!(user && (user.role === roles.admin || user.role === roles.creator));
  }, [user]);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const socketRef = useRef<any>(null);

  const [isNavigatorPopUpOpen, setIsNavigatorPopUpOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [downloadTimer, setDownloadTimer] = useState(0);
  const [printHistoryOrder, setPrintHistoryOrder] = useState<{
    id: string;
    quantity: number;
  }>();
  const [orderForEdition, setOrderForEdition] = useState<{ id: string; }>();

  const [popupData, setPopupData] = useState<IPopupData>();
  const [popupLink, setPopupLink] = useState("");

  // 1. Estados em memória para opções do filtro de data (sem persistência no localStorage)
  const [dateOption, setDateOption] = useState<DateFilterOption>("this_year");
  const [customPeriod, setCustomPeriod] = useState<IPeriod | undefined>(undefined);

  // 2. Inicializa o estado principal 'filter' com valores padrão limpos
  const [filter, setFilter] = useState<IFilter>(() => {
    const now = new Date();
    return {
      statuses: [],
      urgencies: [],
      billing_methods: [],
      solution_types: [],
      period: { from: startOfYear(now), to: endOfYear(now) },
      month: undefined,
    };
  });
  const [totalPage, setTotalPage] = useState(1);
  const [ordersCount, setOrdersCount] = useState(0);
  const [ordersValue, setOrdersValue] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  useEffect(() => {
    if (searchType !== "licensee" || searchTerm.trim().length < 3 || selectedLicenseeId) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get(`/licensees/search?term=${searchTerm}`);
        setSuggestions(data ? data.slice(0, 3) : []);
        setShowSuggestions(true);
      } catch (err) {
        console.error("Erro ao buscar licenciados", err);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, searchType, selectedLicenseeId]);

  useEffect(() => {
    if (editingBillingOrder) {
      setSelectedBillingMethod(editingBillingOrder.billing_method || editingBillingOrder.financial_status || "bill");
    }
  }, [editingBillingOrder]);

  function getDeliveryTerm(date: string, isUrgency: boolean): string {
    let minutesToDeliver = isUrgency ? 180 : 720;
    let termDate = new Date(date);

    function isBusinessDay(date: Date): boolean {
      return date.getDay() !== 0;
    }

    function getNextBusinessHour(date: Date): Date {
      let newDate = new Date(date);
      const day = newDate.getDay();
      const hours = newDate.getHours();

      if (!isBusinessDay(newDate)) {
        newDate = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate() + 1, 8, 0);
      } else if (day === 6 && hours >= 12) {
        newDate = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate() + 2, 8, 0);
      } else if (hours < 8) {
        newDate = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), 8, 0);
      } else if (hours >= 18) {
        newDate = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate() + 1, 8, 0);
      }
      return newDate;
    }

    while (minutesToDeliver > 0) {
      termDate = getNextBusinessHour(termDate);
      const endOfBusinessDay = new Date(termDate.getFullYear(), termDate.getMonth(), termDate.getDate(), 18, 0);
      let minutesToEndOfDay = (endOfBusinessDay.getTime() - termDate.getTime()) / 60000;

      if (minutesToEndOfDay <= 0) {
        termDate = getNextBusinessHour(new Date(termDate.getTime() + 60000));
        continue;
      }

      if (minutesToDeliver <= minutesToEndOfDay) {
        termDate = new Date(termDate.getTime() + minutesToDeliver * 60000);
        minutesToDeliver = 0;
      } else {
        termDate = endOfBusinessDay;
        minutesToDeliver -= minutesToEndOfDay;
      }
    }

    return format(termDate, "dd/MM/yyyy' 'HH:mm");
  }

    const getStatusColor = (status: string) => {
      switch (status) {
        case orderStatus.completed: return "var(--active-green)";
        case orderStatus.cancelled: return "var(--error)";
        case orderStatus.processing: return "var(--gray)";
        case orderStatus.review: return "#D67D11";
        case orderStatus.development: return "var(--development)";
        default: return "var(--gray)";
      }
    };

  const getStatusName = (status: string) => {
    switch (status) {
      case orderStatus.completed: return "Concluído";
      case orderStatus.cancelled: return "Cancelado";
      case orderStatus.processing: return "Não iniciado";
      case orderStatus.review: return "Em análise";
      case orderStatus.development: return "Em desenvolvimento";
      default: return status;
    }
  };

  const formatOrderDate = (dateString: string) => {
    dateString.replace("Z", "");
    return format(new Date(dateString), "dd/MM/yyyy' 'HH:mm");
  };

  const [seeDetails, setSeeDetails] = useState(false)
  const [selectedOrderId, setSelectedOrderId] = useState<string>()

  const [fileChoice, setFileChoice] = useState<IFileChoice>();
  const [contentLength, setContentLength] = useState<number>(0);
  const [received, setReceived] = useState<number>(0);

  const [providerSelected, setIsProviderSelected] = useState(false);
  const [orderPrintManagement, setOrderPrintManagement] = useState<IOrder>();
  const [printMenuAnchor, setPrintMenuAnchor] = useState<{ element: HTMLElement, order: IOrder } | null>(null);

  const [notPaidOrders, setNotPaidOrders] = useState(0);
  const [totalGcode, setTotalGcode] = useState(0);

  const [uploadingPlanit, setUploadingPlanit] = useState<string>();

  const [printHasStarted, setPrintHasStarted] = useState(false);

  function resumePrinting() {
    setReceived(contentLength);
  }

  const cost = popupData?.cost?.toString() || "0";
  const costFormatted = parseFloat(cost).toLocaleString("pt-br", {
    style: "currency",
    currency: currency?.code ?? "BRL",
  });

  function loadMore() {
    setPage(page + 1);
  }

  const proceedPrintChoice = useCallback(
    async (fileChoice: IFileChoice) => {
      if (!fileChoice.printer || !fileChoice.file_streams.length) {
        if (!defaultPrinter?.value) {
          window.alert(i18n.t("orders.printChoose.noprinter"));
          return;
        }

        if (!fileChoice?.side || !fileChoice?.solution_standard?.id) {
          showToast({
            type: "error",
            message: "Dados incompletos",
          });
          return;
        }

        const { data } = await api.patch(`/file-choice/${fileChoice.id}`, {
          printer_id: defaultPrinter.value,
          side: fileChoice.side,
          solution_standard_id: fileChoice.solution_standard.id,
        });

        setFileChoice(data);
        return;
      }
      setFileChoice(fileChoice);
    },
    [defaultPrinter?.value]
  );

  const savePrintChoice = useCallback(
    async (formData: IPrintChoiceFormData) => {
      if (!formData.standard || !formData.side || !formData.printer_id) {
        showToast({
          type: "error",
          message: "Dados incompletos",
        });
        return;
      }

      try {
        const { data } = await api.post("/file-choice", {
          order_id: formData.order_id,
          printer_id: formData.printer_id,
          side: formData.side,
          solution_standard_id: formData.standard.id,
        });
        setFileChoice(data);
      } catch (e: any) {
        e?.response?.data?.message &&
          showToast({
            type: "error",
            message: i18n.t(e.response.data.message),
          });
      }
    },
    []
  );

  const addPrintChoice = useCallback(
    async (formData: IPrintChoiceFormData) => {
      if (
        !formData.standard ||
        !formData.side ||
        (userHasPrinter &&
          !formData.printer_id)
      ) {
        showToast({
          type: "error",
          message: "Dados incompletos",
        });
        return;
      }

      try {
        const { data } = await api.post("/file-choice", {
          order_id: formData.order_id,
          printer_id: formData.printer_id,
          side: formData.side,
          solution_standard_id: formData.standard.id,
        });
        return data;
      } catch (e: any) {
        e?.response?.data?.message &&
          showToast({
            type: "error",
            message: i18n.t(e.response.data.message),
          });
      }
    },
    [userHasPrinter]
  );

  const getProductionQueueOrders = useCallback(async () => {
    const role = user?.role;
    const isAdminOrCreator = role === roles.admin || role === roles.creator;
    if (!isAdminOrCreator) return;

    setLoadingProductionQueue(true);
    try {
      const queryList = [
        "status=processing",
        "status=development",
        "status=review",
        "limit=100"
      ];

      const data = await getOrdersService({ queryList });
      setProductionQueueOrders(data?.data || []);
    } catch (error) {
      console.error("Erro ao buscar fila de produção ativa", error);
    } finally {
      setLoadingProductionQueue(false);
    }
  }, [
    user,
    getOrdersService
  ]);

  const getOrders = useCallback(
    async (reload: boolean, currentSearchQuery?: string, currentLicenseeId?: string | null) => {
      setLoading(true);

      if (reload && user && [roles.admin, roles.creator].includes(user.role)) {
        getProductionQueueOrders();
      }

      const queryList = [];
      const activeSearch = currentSearchQuery !== undefined ? currentSearchQuery : searchQuery;
      const activeLicenseeId = currentLicenseeId !== undefined ? currentLicenseeId : selectedLicenseeId;

      const isSearchById = searchType === "number" && !!activeSearch;

      if (isSearchById) {
        queryList.push(`search=${activeSearch}`);
      } else {
        if (activeSearch) {
          if (searchType === "licensee") {
            if (activeLicenseeId) {
              queryList.push(`licensee=${activeLicenseeId}`);
            }
          } else if (searchType === "patient") {
            queryList.push(`patient_name=${activeSearch}`);
          }
        }

        if (filter?.month !== undefined) {
          const [month, year] = format(filter?.month, "MM/yyyy").split("/");
          queryList.push(`month=${parseInt(month)}`);
          queryList.push(`year=${year}`);
        } else {
          filter?.period?.from !== undefined &&
            queryList.push(`from=${filter?.period?.from.toJSON()}`);
          filter?.period?.to !== undefined &&
            queryList.push(`to=${filter?.period?.to?.toJSON()}`);
        }
        filter?.order_type &&
          queryList.push(`order_type=${filter.order_type.value}`);
        if (filter?.solution_types && filter.solution_types.length > 0) {
          filter.solution_types.forEach(type => {
            queryList.push(`solution_type=${type}`);
          });
        } else if (filter?.solution_type) {
          queryList.push(`solution_type=${filter.solution_type.value}`);
        }
        filter?.solution && queryList.push(`solution=${filter.solution.id}`);
        if (filter?.statuses && filter.statuses.length > 0) {
          filter.statuses.forEach(status => {
            queryList.push(`status=${status}`);
          });
        }
        if (filter?.urgencies && filter.urgencies.length > 0) {
          filter.urgencies.forEach(urg => {
            if (urg === "urgent") {
              queryList.push(`urgency_type=requested`);
              queryList.push(`urgency_type=approved`);
            } else if (urg === "normal") {
              queryList.push(`urgency_type=normal`);
            }
          });
        }

        filter?.licensee && queryList.push(`licensee=${filter.licensee.id}`);
        filter?.person && queryList.push(`personUserId=${filter.person.id}`);
        if (filter?.billing_methods && filter.billing_methods.length > 0) {
          filter.billing_methods.forEach(method => {
            queryList.push(`billing_method=${method}`);
          });
        } else if (filter?.billing_method) {
          queryList.push(`billing_method=${filter.billing_method}`);
        }
      }

      queryList.push(`limit=${limit}`);
      !reload && queryList.push(`page=${page}`);

      try {
        const data = await getOrdersService({ queryList });
        const fetchedData = data.data || [];

        setTotalPage(data.total / limit);
        setOrdersCount(data.total);
        setOrders(reload ? fetchedData : (prevOrders) => [...prevOrders, ...fetchedData]);;
        setOrdersValue(data.sum);
        setNotPaidOrders(data.totalNotPaid);
        setTotalGcode(data.totalGcode);
      } catch (error) {
        showToast({
          type: "error",
          message: "Ocorreu um erro ao tentar carregar os pedidos",
        });
      } finally {
        setLoading(false);
      }
    },
    [
      filter,
      getOrdersService,
      limit,
      page,
      searchQuery,
      searchType,
      selectedLicenseeId,
      user,
      getProductionQueueOrders,
    ]
  );

  const getOrdersRef = useRef(getOrders);
  getOrdersRef.current = getOrders;

  useEffect(() => {
    const role = user?.role;
    const isAdminOrCreator = role === roles.admin || role === roles.creator;
    
    if (!isRealTimeActive || !isAdminOrCreator) {
      if (socketRef.current) {
        console.log("🔌 Desconectando socket do Live Mode.");
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsSocketConnected(false);
      }
      return;
    }

    const token = localStorage.getItem("@FixitApp:token");
    const socketUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080";

    console.log("🔌 Conectando ao socket do Live Mode:", socketUrl);
    
    const socket = io(socketUrl, {
      auth: { token },
      transports: ["websocket"],
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("🔌 Socket conectado com sucesso!");
      setIsSocketConnected(true);
    });

    socket.on("disconnect", () => {
      console.log("🔌 Socket desconectado.");
      setIsSocketConnected(false);
    });

    socket.on("order_created", (payload) => {
      console.log("🔔 [Live Mode] Novo pedido criado!", payload);
      getOrdersRef.current(true);
    });

    socket.on("order_updated", (payload) => {
      console.log("🔄 [Live Mode] Pedido atualizado!", payload);
      getOrdersRef.current(true);
    });

    socket.on("connect_error", (error) => {
      console.error("🔌 Erro na conexão do socket:", error);
      setIsSocketConnected(false);
    });

    return () => {
      if (socketRef.current) {
        console.log("🔌 Efetuando limpeza da conexão do socket.");
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsSocketConnected(false);
      }
    };
  }, [isRealTimeActive, user?.role]);

  const getOrdersForDownload = useCallback(async (): Promise<IOrder[]> => {
    const queryList = [];

    const isSearchById = searchType === "number" && !!searchQuery;

    if (isSearchById) {
      queryList.push(`search=${searchQuery}`);
    } else {
      if (searchQuery) {
        if (searchType === "licensee") {
          if (selectedLicenseeId) {
            queryList.push(`licensee=${selectedLicenseeId}`);
          }
        } else if (searchType === "patient") {
          queryList.push(`patient_name=${searchQuery}`);
        }
      }

      if (filter?.month !== undefined) {
        const [month, year] = format(filter?.month, "MM/yyyy").split("/");
        queryList.push(`month=${parseInt(month)}`);
        queryList.push(`year=${year}`);
      } else {
        filter?.period?.from !== undefined &&
          queryList.push(`from=${filter?.period?.from.toJSON()}`);
        filter?.period?.to !== undefined &&
          queryList.push(`to=${filter?.period?.to?.toJSON()}`);
      }
      filter?.order_type &&
        queryList.push(`order_type=${filter.order_type.value}`);
      if (filter?.solution_types && filter.solution_types.length > 0) {
        filter.solution_types.forEach(type => {
          queryList.push(`solution_type=${type}`);
        });
      } else if (filter?.solution_type) {
        queryList.push(`solution_type=${filter.solution_type.value}`);
      }
      filter?.solution && queryList.push(`solution=${filter.solution.id}`);
      if (filter?.statuses && filter.statuses.length > 0) {
        filter.statuses.forEach(status => {
          queryList.push(`status=${status}`);
        });
      }
      if (filter?.urgencies && filter.urgencies.length > 0) {
        filter.urgencies.forEach(urg => {
          if (urg === "urgent") {
            queryList.push(`urgency_type=requested`);
            queryList.push(`urgency_type=approved`);
          } else if (urg === "normal") {
            queryList.push(`urgency_type=normal`);
          }
        });
      }

      filter?.licensee && queryList.push(`licensee=${filter.licensee.id}`);
      filter?.person && queryList.push(`personUserId=${filter.person.id}`);
      if (filter?.billing_methods && filter.billing_methods.length > 0) {
        filter.billing_methods.forEach(method => {
          queryList.push(`billing_method=${method}`);
        });
      } else if (filter?.billing_method) {
        queryList.push(`billing_method=${filter.billing_method}`);
      }
    }

    queryList.push(`limit=${ordersCount}`);
    queryList.push(`page=${page}`);

    try {
      var data = await getOrdersService({ queryList });
      return data.data || [];
    } catch (error) {
      showToast({
        type: "error",
        message: "Não foi possivel realizar download",
      });
    }

    return [];
  }, [
    filter,
    getOrdersService,
    ordersCount,
    page,
    searchQuery,
    searchType,
    selectedLicenseeId,
  ]);

  async function downloadOrders() {
    const allOrdersFromSelectedPeriod = await getOrdersForDownload();

    const xlsData = new OrdersToXlsFormatFactory({
      orders: allOrdersFromSelectedPeriod,
      role: user.role,
    }).formatJsonToXls();

    const workSheet = utils.json_to_sheet(xlsData);
    const workBook = utils.book_new();

    const workSheetName = filter?.month
      ? format(filter?.month, "MM.yyyy")
      : filter?.period?.from && filter.period.to
        ? `${format(filter.period.from, "dd.MM.yyyy")} - ${format(
          filter.period.to,
          "dd.MM.yyyy"
        )}`
        : "Fix it";

    utils.book_append_sheet(workBook, workSheet, workSheetName);

    utils.sheet_add_aoa(
      workSheet,
      xlsxColumnsByUserRole[
      user.role !== roles.fixiter && user.role !== roles.employee
        ? "DEFAULT"
        : (user.role as keyof typeof xlsxColumnsByUserRole)
      ],
      {
        origin: "A1",
      }
    );

    writeFile(workBook, `${i18n.t("sidebar.requests")}.xlsx`);
  }

  const openPrintHistory = useCallback(
    ({ orderId, quantity }: { orderId: string; quantity: number }) => {
      setPrintHistoryOrder({
        id: orderId,
        quantity,
      });
    },
    []
  );

  const openOrderEdit = useCallback(({ orderId }: { orderId: string }) => {
    setOrderForEdition({
      id: orderId,
    })
  }, [])

  useEffect(() => {
    if (downloadTimer > 0) {
      setTimeout(() => setDownloadTimer(downloadTimer - 1), 1000);
    }

    return () => {
      setDownloadTimer(0);
    };
  }, [downloadTimer]);

  const printOrderPopup = useCallback(
    (order: any) => {
      if (
        !defaultPrinter &&
        (userHasPrinter)
      ) {
        window.alert(i18n.t("orders.printChoose.noprinter"));
        return;
      }
      setPopupData({
        order,
        type: "print",
        printer: defaultPrinter
          ? {
            id: defaultPrinter.value,
            name: defaultPrinter.name,
            xyz: defaultPrinter.xyz,
          }
          : undefined,
      });
      setIsPopupOpen(true);
    },
    [defaultPrinter, userHasPrinter]
  );

  // aqui
  const printFilePopup = useCallback(
    ({ order, printTime, cost, weight, printer, type }: IPopupData) => {
      setDownloadTimer(5);
      setPopupData({
        order,
        type,
        printTime,
        weight,
        cost,
        printer,
      });
      // opcao 1
      if (isChrome === true) {
        setIsPopupOpen(true);
      } else {
        setIsNavigatorPopUpOpen(true);
      }
    },
    []
  );

  function formatDate(date: Date) {
    return format(date, "dd/MM/yyyy");
  }

  const openPlanitFileUploadPopup = useCallback((planit_id: string) => {
    setUploadingPlanit(planit_id);
  }, []);

  const openPopup = useCallback(
    ({ order, printTime, cost, weight, link, printer, type }: IPopupData) => {
      if (!link) {
        return;
      }
      setDownloadTimer(5);
      setPopupData({
        order,
        type,
        printTime,
        weight,
        cost,
        printer,
      });
      setIsPopupOpen(true);
      setPopupLink(link);
    },
    []
  );

  useEffect(() => {
    // é possível testar pelo page == 1 ao inves de uma flag reload, então tirar o if (page > 1) e aqui apenas fazer o set Page.
    if (!!filter) {
      setPage(1);
      getOrders(true);
    }
  }, [filter]);

  useEffect(() => {
    if (page > 1) {
      getOrders(false);
    }
  }, [page]);

  useEffect(() => {
    if (closure && location.search?.length) {
      const queryParams = new URLSearchParams(location.search);
      const closureFilterFrom = queryParams.getAll("from")[0].split("/");
      const closureFilterTo = queryParams.getAll("to")[0].split("/");
      setFilter((prevState) => ({
        ...prevState,
        month: undefined,
        period: {
          from: new Date(
            parseInt(closureFilterFrom[2]),
            parseInt(closureFilterFrom[1]) - 1,
            parseInt(closureFilterFrom[0])
          ),
          to: new Date(
            parseInt(closureFilterTo[2]),
            parseInt(closureFilterTo[1]) - 1,
            parseInt(closureFilterTo[0])
          ),
        },
      }));
    } else {
      setFilter((prevState) => ({
        ...prevState,
        month: undefined,
        period: {
          from: startOfYear(new Date()),
          to: endOfYear(new Date())
        },
      }));
    }

    return () => {
      setFilter({});
    };
  }, [location.search, closure]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchType === "licensee") {
      return;
    }
    setSearchQuery(searchTerm);
    setPage(1);
    getOrders(true, searchTerm);

    setIsProductionQueueExpanded(false);
  }, [searchTerm, searchType, getOrders, setIsProductionQueueExpanded]);

  const handleSearchTypeChange = useCallback(
    (type: "number" | "licensee" | "patient") => {
      setSearchType(type);
      setSearchTerm("");
      setSearchQuery("");
      setSelectedLicenseeId(null);
      setSuggestions([]);
      setShowSuggestions(false);
      setPage(1);
      getOrders(true, "");
    },
    [getOrders]
  );

  const handleSelectSuggestion = useCallback((suggestion: any) => {
    setSearchTerm(suggestion.name);
    setSearchQuery(suggestion.name);
    setSelectedLicenseeId(suggestion.id);
    setSuggestions([]);
    setShowSuggestions(false);
    setPage(1);
    getOrders(true, suggestion.name, suggestion.id);

    setIsProductionQueueExpanded(false);
  }, [getOrders, setIsProductionQueueExpanded]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setSearchQuery("");
    setSelectedLicenseeId(null);
    setSuggestions([]);
    setShowSuggestions(false);
    setPage(1);
    getOrders(true, "", null);
  }, [getOrders]);

  useEffect(() => {
    if (location.state?.orderId) {
      setSelectedOrderId(location.state.orderId);
      setSeeDetails(true);
    }
  }, [location.state]);

  const handleFilterApply = useCallback((selections: Record<string, any>) => {
    setFilter(prev => ({
      ...prev,
      statuses: selections["status"] || [],
      urgencies: selections["urgency"] || [],
      billing_methods: selections["billing_method"] || [],
      solution_types: selections["solution_type"] || [],
    }));
    setPage(1);

    setIsProductionQueueExpanded(false);
  }, [setIsProductionQueueExpanded]);

  useEffect(() => {
    if (seeDetails) return;

    setNavigationAction({
      hasButton: false,
      description: (
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          {i18n.t("orders.header") as string}
        </div>
      ) as any,
      customElement: (
        <HeaderActions>
          <SearchContainer onSubmit={handleSearch}>
            {canSelectSearchType && (
              <SearchSelectWrapper>
                <SearchSelectButton
                  id="search-select-button"
                  aria-controls={openSearchMenu ? "search-select-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={openSearchMenu ? "true" : undefined}
                  onClick={(e) => setSearchMenuAnchorEl(e.currentTarget)}
                  type="button"
                >
                  {searchType === "number"
                    ? "Nº Pedido"
                    : searchType === "licensee"
                    ? "Licenciado"
                    : "Paciente"}
                  {openSearchMenu ? <FiChevronUp /> : <FiChevronDown />}
                </SearchSelectButton>
                <Menu
                  id="search-select-menu"
                  anchorEl={searchMenuAnchorEl}
                  open={openSearchMenu}
                  onClose={() => setSearchMenuAnchorEl(null)}
                  MenuListProps={{
                    "aria-labelledby": "search-select-button",
                  }}
                  PaperProps={{
                    sx: {
                      borderRadius: "12px",
                      marginTop: "4px",
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                      border: "1px solid rgba(0, 0, 0, 0.05)",
                    }
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleSearchTypeChange("number");
                      setSearchMenuAnchorEl(null);
                    }}
                    sx={{
                      fontSize: "14px",
                      fontWeight: searchType === "number" ? 600 : 500,
                      color: searchType === "number" ? "var(--fixit)" : "var(--text)",
                      padding: "8px 24px",
                    }}
                  >
                    Nº Pedido
                  </MenuItem>
                  {canSearchByLicensee && (
                    <MenuItem
                      onClick={() => {
                        handleSearchTypeChange("licensee");
                        setSearchMenuAnchorEl(null);
                      }}
                      sx={{
                        fontSize: "14px",
                        fontWeight: searchType === "licensee" ? 600 : 500,
                        color: searchType === "licensee" ? "var(--fixit)" : "var(--text)",
                        padding: "8px 24px",
                      }}
                    >
                      Licenciado
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      handleSearchTypeChange("patient");
                      setSearchMenuAnchorEl(null);
                    }}
                    sx={{
                      fontSize: "14px",
                      fontWeight: searchType === "patient" ? 600 : 500,
                      color: searchType === "patient" ? "var(--fixit)" : "var(--text)",
                      padding: "8px 24px",
                    }}
                  >
                    Paciente
                  </MenuItem>
                </Menu>
              </SearchSelectWrapper>
            )}
            {searchQuery ? (
              <SelectedFilterBadge>
                <SelectedFilterTag>
                  <button type="button" onClick={handleClearSearch}>
                    <RiCloseFill />
                  </button>
                  <span>{searchQuery}</span>
                </SelectedFilterTag>
              </SelectedFilterBadge>
            ) : (
              <SearchInput
                type="text"
                pattern={searchType === "number" ? "[0-9]*" : undefined}
                inputMode={searchType === "number" ? "numeric" : "text"}
                placeholder="Procurar pedido..."
                value={searchTerm}
                onChange={(e) => {
                  if (searchType === "number") {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    setSearchTerm(numericValue);
                  } else {
                    setSearchTerm(e.target.value);
                    if (searchType === "licensee") {
                      setSelectedLicenseeId(null);
                    }
                  }
                }}
                $hasText={!!searchTerm.trim()}
              />
            )}
            {showSuggestions && suggestions.length > 0 && (
              <SearchSuggestionsList>
                {suggestions.map((suggestion) => (
                  <SearchSuggestionItem
                    key={suggestion.id}
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    {suggestion.name}
                  </SearchSuggestionItem>
                ))}
              </SearchSuggestionsList>
            )}
            <SearchButton type="submit" $disabled={searchType === "licensee"}>
              <RiSearchLine />
            </SearchButton>
          </SearchContainer>

          <DropdownFilter
            multiple={true}
            layout="orders"
            groups={[
              {
                id: "status",
                title: "Status do Pedido",
                options: [
                  { label: "Concluído", value: orderStatus.completed },
                  { label: "Em desenvolvimento", value: orderStatus.development },
                  { label: "Não iniciado", value: orderStatus.processing },
                  { label: "Em análise", value: orderStatus.review },
                  { label: "Cancelado", value: orderStatus.cancelled },
                ],
              },
              {
                id: "urgency",
                title: "Urgência do Pedido",
                options: [
                  { label: "Urgente", value: "urgent" },
                  { label: "Normal", value: "normal" },
                ],
              },
              {
                id: "solution_type",
                title: "Tipo de Pedido",
                options: [
                  { label: "Padronizado", value: solutionTypes.standard },
                  { label: "Personalizado", value: solutionTypes.custom },
                ],
              },
              {
                id: "billing_method",
                title: "Método de Faturamento",
                options: [
                  { label: "FixCoins", value: "fixcoin" },
                  { label: "Fatura", value: "bill" },
                  { label: "Cortesia", value: "courtesy" },
                ],
              }
            ]}
            defaultValues={{
              status: filter?.statuses || [],
              urgency: filter?.urgencies || [],
              billing_method: filter?.billing_methods || [],
              solution_type: filter?.solution_types || [],
            }}
            onApply={handleFilterApply}
          />

          <DateDropdownFilter
            defaultOption={dateOption}
            defaultPeriod={customPeriod}
            onApply={(period, option) => {
              setDateOption(option);
              setCustomPeriod(option === "custom" && period ? period : undefined);

              setFilter(prev => ({ 
                ...prev, 
                period: option === "this_month" ? undefined : period, 
                month: option === "this_month" ? new Date() : undefined 
              }));
              setPage(1);

              setIsProductionQueueExpanded(false);
            }}
          />

        </HeaderActions>
      ),
    });
  }, [setNavigationAction, filter, searchTerm, searchType, canSearchByLicensee, canSelectSearchType, searchMenuAnchorEl, openSearchMenu, suggestions, showSuggestions, handleSelectSuggestion, handleSearchTypeChange, handleSearch, handleFilterApply, navigate, user, seeDetails, handleClearSearch, selectedLicenseeId, searchQuery, dateOption, customPeriod, isRealTimeActive, isSocketConnected, setIsProductionQueueExpanded]);

  const sortedProductionQueueOrders = useMemo(() => {
    if (!Array.isArray(productionQueueOrders)) return [];
    
    return [...productionQueueOrders].sort((a, b) => {
      const isAUrgent = a && (a.urgency_type === orderUrgencyTypes.requested || a.urgency_type === orderUrgencyTypes.approved);
      const isBUrgent = b && (b.urgency_type === orderUrgencyTypes.requested || b.urgency_type === orderUrgencyTypes.approved);
      
      if (isAUrgent && !isBUrgent) return -1;
      if (!isAUrgent && isBUrgent) return 1;
      return 0;
    });
  }, [productionQueueOrders]);

  const otherOrders = useMemo(() => {
    if (!Array.isArray(orders)) return [];
    if (!isProductionQueueExpanded) return orders;
    const activeIds = new Set((productionQueueOrders || []).filter(o => o && o.id).map(o => o.id));
    return orders.filter(o => o && o.id && !activeIds.has(o.id));
  }, [orders, productionQueueOrders, isProductionQueueExpanded]);

  const columns: Column<IOrder>[] = [
    {
      key: "id",
      header: "Identificação do pedido",
      width: "35%",
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
             <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
              <strong style={{ color: getStatusColor(row.status), fontSize: '15px' }}>#{row.incremental_id || row.id.substring(0, 4)}</strong>
              <span style={{ fontWeight: 600, fontSize: '15px', color: getStatusColor(row.status) }}>{row.solution?.name || "Pedido"}</span>
              {(row.urgency_type === orderUrgencyTypes.requested || row.urgency_type === orderUrgencyTypes.approved) && (
              <UrgencyBadge><RiAlarmWarningFill size={10} /> <span className="badge-text">URGENTE</span></UrgencyBadge>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray)', fontSize: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <FiCalendar /> {formatOrderDate(row.created_at)}
            </div>
            <span>•</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--gray)' }}>
                {row.solution_type === solutionTypes.standard ? <FiBox size={12} /> : <RiRulerLine size={12} />} {row.solution_type === solutionTypes.standard ? "Padronizado" : "Personalizado"}
              </div>
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: 'center', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: getStatusColor(row.status), fontWeight: 600, fontSize: '13px' }}>
            <StatusBolinha 
              color={getStatusColor(row.status)} 
              shouldPulse={
                row.status === orderStatus.development || 
                row.status === orderStatus.review
              }
            /> {getStatusName(row.status)}
          </div>
          {row.status !== orderStatus.cancelled && (
            <div style={{ fontSize: '12px', color: 'var(--gray)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {row.solution_type === solutionTypes.standard ? (
                   <><RiFlashlightFill size={12} /> Imediato</>
                ) : row.status === orderStatus.completed ? (
                   <><FiCheck size={12}/> em: {formatOrderDate((row.files && row.files.length > 0 && row.files[0].created_at) ? row.files[0].created_at : row.created_at)}</>
                ) : (
                   <><RiHourglassFill size={12}/> prazo: {getDeliveryTerm(row.created_at, row.urgency_type !== orderUrgencyTypes.normal)}</>
                )}
            </div>
          )}
        </div>
      )
    },
    {
      key: "client",
      header: user.role === roles.admin || user.role === roles.saf || user.role === roles.creator ? "Cliente" : "Paciente",
      render: (row) => {
        const isStaff = user.role === roles.admin || user.role === roles.saf || user.role === roles.creator;

        if (isStaff) {
          const secondaryInfos: string[] = [];
          
          if (row.requester_id && row.requester?.name) {
            secondaryInfos.push(`Fixiter: ${abridgedControl(row.requester.name, 15)}`);
          }
          if (row.solution_type !== solutionTypes.standard) {
            secondaryInfos.push(`Paciente: ${row.patient?.name ? abridgedControl(row.patient.name, 15) : "Não informado"}`);
          }

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--black)' }}>
                {row.licensee?.name ? abridgedControl(row.licensee.name, 18) : "-"}
              </span>
              
              {secondaryInfos.length > 0 && (
                <span style={{ fontSize: '13px', color: 'var(--gray)', marginTop: '2px' }}>
                  {secondaryInfos.join(" • ")}
                </span>
              )}
            </div>
          );
        } else {
          const isFixiter = user.role === roles.fixiter;

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--black)' }}>
                {row.patient?.name ? abridgedControl(row.patient.name, 15) : ""}
              </span>
              
              {!isFixiter && row.requester_id && row.requester?.name && (
                <span style={{ fontSize: '13px', color: 'var(--gray)', marginTop: '2px' }}>
                  Fixiter: {abridgedControl(row.requester.name, 15)}
                </span>
              )}
            </div>
          );
        }
      }
    },
    {
      key: "total",
      header: "Total",
      render: (row) => {
        const isCancelled = row.status === orderStatus.cancelled;
        const isCourtesy = row.billing_method === "courtesy" || row.financial_status === orderSituation.courtesy;

        const formattedPrice = parseFloat((row.total || 0).toString()).toLocaleString("pt-br", {
          style: "currency",
          currency: currency?.code ?? "BRL"
        });

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'center', height: '100%' }}>
            <span style={{ 
              fontWeight: isCancelled || isCourtesy ? 500 : 600, 
              color: isCancelled || isCourtesy ? 'var(--gray)' : 'var(--black)',
              textDecoration: isCancelled || isCourtesy ? 'line-through' : 'none',
              fontSize: '14px'
            }}>
              {formattedPrice}
            </span>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {isCourtesy ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: 'var(--active-green)' }}>
                  <FiGift size={14} /> Cortesia
                </span>
              ) : (
                <>
                  {(row.billing_method === "fixcoin" || row.financial_status === "fixcoin_paid") && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: isCancelled ? 'var(--gray)' : 'var(--fixit)' }}>
                      <RiMoneyDollarCircleLine size={14} /> FixCoins {isCancelled && "(Estornado)"}
                    </span>
                  )}
                  {(row.billing_method === "bill" || row.financial_status === "not_paid") && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: isCancelled ? 'var(--gray)' : 'var(--development)' }}>
                      <RiFileListLine size={14} /> Fatura {isCancelled && "(Cancelado)"}
                    </span>
                  )}
                  {(row.billing_method === "paid" || row.financial_status === "paid") && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: isCancelled ? 'var(--gray)' : 'var(--active-green)' }}>
                      <FiCheck size={14} /> Pago {isCancelled && "(Cancelado)"}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        );
      }
    }
  ];

  // Substitui a coluna "Total" pela coluna "FixPoint" para a role FIXITER
  if (user.role === roles.fixiter) {
    const totalIndex = columns.findIndex(col => col.key === "total");
    if (totalIndex !== -1) {
      columns[totalIndex] = {
        key: "fixpoint",
        header: "FixPoint",
        render: (row) => {
          const name = row.licensee?.name || row.licensee?.companyName;
          return (
            <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--black)' }}>
              {name ? abridgedControl(name, 15) : "-"}
            </span>
          );
        }
      };
    }
  }

  const actions: TableAction<IOrder>[] = [
      {
        label: "Gestão de Impressão",
        icon: <RiPrinterLine size={20} />,
        position: "outside",
        hidden: (row) => 
          (user.role !== roles.admin && user.role !== roles.saf && user.role !== roles.creator) ||
          row.status === orderStatus.cancelled || 
          row.status === orderStatus.processing || 
          row.status === orderStatus.development,
        disabled: (row) => row.status !== orderStatus.completed,
        onClick: (row) => {
          setOrderPrintManagement(row);
        }
      },
      {
        label: "Imprimir",
        icon: <RiPrinterLine size={20} />,
        position: "outside",
        hidden: (row) => 
          ((isMobile || window.innerWidth <= 768) && user.role === roles.licensee) || 
          (user.role !== roles.licensee && user.role !== roles.employee) ||  
          (row.solution_type !== solutionTypes.standard && (!row.files || row.files.length === 0 || row.status !== orderStatus.completed)) ||
          row.status === orderStatus.cancelled || 
          row.status === orderStatus.processing || 
          row.status === orderStatus.development,
        disabled: (row) => row.status !== orderStatus.completed,
        onClick: (row, event) => {
        if (event) {
          setPrintMenuAnchor({ element: event.currentTarget as HTMLElement, order: row });
        }
      }
    },
    {
      label: "Ver Pedido",
      icon: <FiEye size={16} />,
      onClick: (row) => {
        setSelectedOrderId(row.id);
        setSeeDetails(true);
      },
    },
    {
      label: "Editar Pedido",
      icon: <FiEdit2 size={16} />,
      hidden: (row) => row.solution_type === solutionTypes.standard,
      disabled: (row) => (
        row.status === orderStatus.cancelled ||
        row.quantity === row.file_choices?.filter(f => f.completed).length || 
        (row.status !== orderStatus.processing && row.status !== orderStatus.review)
      ),
      onClick: (row) => {
        openOrderEdit({ orderId: row.id });
      },
    },
      {
        label: "Subir Arquivo",
        icon: <RiToolsFill size={16} />,
        hidden: (row) => user.role !== roles.creator || row.solution_type === solutionTypes.standard,
        disabled: (row) => row.status === orderStatus.cancelled,
        onClick: (row) => {
          setUploadingCustomOrder(row);
        },
      },
      {
        label: "Alterar Faturamento",
      icon: <RiMoneyDollarCircleLine size={16} />,
      hidden: (row) => user.role !== roles.admin && user.role !== roles.saf, 
      onClick: (row) => {
        setEditingBillingOrder(row);
      },
    },
    {
      label: "Cancelar Pedido",
      icon: <MdOutlineCancel size={16} color="var(--error)" />,
      disabled: (row) => (
        row.status === orderStatus.cancelled ||
        row.quantity === row.file_choices?.filter(f => f.completed).length ||
        (row.solution_type !== solutionTypes.standard && row.status !== orderStatus.processing && row.status !== orderStatus.review)
      ),
      onClick: async (row) => {
        if (window.confirm(i18n.t("orders.cancelConfirmation"))) {
          try {
            await api.patch(`/orders/${row.id}`, { status: orderStatus.cancelled });
            showToast({ type: "success", message: i18n.t("optionsBtn.successMessage") });
            getOrders(true);
          } catch (e) {
            showToast({ type: "error", message: "Erro ao cancelar pedido" });
          }
        }
      },
    }
  ];

  return (
    <>
      {seeDetails ?
        <>
          {
            (user.role === roles.licensee || user.role === roles.employee || user.role === roles.fixiter) ?
              <Evaluation
                toggleSeeDetails={() => {
                  setSeeDetails((prevState) => !prevState)
                }}
                order_id={selectedOrderId ?? ""} />
              :
              <ShowOrder
                order_id={selectedOrderId ?? ""}
                toggleSeeDetails={() => {
                  setSeeDetails((prevState) => !prevState)
                }}

              />
          }
        </>
        :
        <>
          {!!uploadingPlanit && (
            <PlanitUploadingPopUpModal
              open={!!uploadingPlanit}
              planit_id={uploadingPlanit}
              close={() => setUploadingPlanit(undefined)}
            />
          )}
          {!!uploadingCustomOrder && (
            <CustomOrderUploadForm
              order={uploadingCustomOrder}
              onUpdate={(updatedOrder) => {
                setUploadingCustomOrder(undefined);
                getOrders(true);
              }}
              onClose={() => setUploadingCustomOrder(undefined)}
            />
          )}
          {!!orderPrintManagement && (
            <PrintManagementPopUpModal
              inheritOverflow
              order={orderPrintManagement}
              isOpen={!!orderPrintManagement}
              onRequestClose={() => {
                setOrderPrintManagement(undefined);
              }}
            />
          )}
          {!!printHistoryOrder && (
            <PrintHistoryPopUpModal
              inheritOverflow
              quantity={printHistoryOrder.quantity}
              orderId={printHistoryOrder.id}
              isOpen={!!printHistoryOrder}
              onRequestClose={() => {
                setPrintHistoryOrder(undefined);
              }}
            />
          )}
          <Menu
            anchorEl={printMenuAnchor?.element}
            open={Boolean(printMenuAnchor)}
            onClose={() => setPrintMenuAnchor(null)}
            PaperProps={{
              style: {
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
              },
            }}
          >
              <MenuItem 
                onClick={() => {
                  if (printMenuAnchor?.order) printOrderPopup(printMenuAnchor.order);
                  setPrintMenuAnchor(null);
                }}
                style={{ display: "flex", gap: "8px", fontSize: "14px" }}
              >
                <RiPrinterLine size={18} />
                Controle de impressões
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  if (printMenuAnchor?.order) openPrintHistory({ orderId: printMenuAnchor.order.id, quantity: printMenuAnchor.order.quantity });
                  setPrintMenuAnchor(null);
                }}
                style={{ display: "flex", gap: "8px", fontSize: "14px", ...(printMenuAnchor?.order?.solution_type !== solutionTypes.standard && { display: 'none' }) }}
              >
                <RiHistoryLine size={18} />
                Histórico de impressão
              </MenuItem>
          </Menu>
          {isPopupOpen && popupData && (
            <Modal
              inheritOverflow
              open={isPopupOpen}
              onRequestClose={() => {
                if (printHasStarted) {
                  showToast({
                    type: "info",
                    message: `Bloqueado enquanto a operação está em progresso`,
                  });
                  return;
                }
                setFileChoice(undefined);
                setIsPopupOpen(false);
                getOrders(true);
              }}
            >
              <PopupContentWrapper>
                {popupData.type === "download" ? (
                  <DownloadFile
                    popupData={popupData}
                    popupLink={popupLink}
                    costFormatted={costFormatted}
                    downloadTimer={downloadTimer}
                  />
                ) : !fileChoice ? (
                  <PrintChoose
                    order={popupData.order}
                    printer={popupData.printer}
                    onProceed={proceedPrintChoice}
                    onSelect={savePrintChoice}
                    onAdd={addPrintChoice}
                  />
                ) : !fileChoice.solution_standard ? (
                  <CustomPrintControl
                    fileChoice={fileChoice}
                    setPrintHasStarted={(value) => setPrintHasStarted(value)}
                  />
                ) : (
                  <PrintControl
                    fileChoice={fileChoice}
                    setPrintHasStarted={(value) => setPrintHasStarted(value)}
                  />
                )}
              </PopupContentWrapper>
            </Modal>
          )}
          <Container>
            {!!editingBillingOrder && (
              <FormModal
                open={true}
                onRequestClose={() => !isChangingBilling && setEditingBillingOrder(undefined)}
                title="Alterar Faturamento"
                subtitle={`Escolha o método de cobrança para o pedido #${editingBillingOrder.incremental_id || editingBillingOrder.id.substring(0, 4)}`}
                width={450}
                onSave={async () => {
                  setIsChangingBilling(true);
                  try {
                    await api.put(`/orders/${editingBillingOrder.id}/billing_method`, { billing_method: selectedBillingMethod });
                    showToast({ type: "success", message: "Faturamento alterado com sucesso" });
                    setEditingBillingOrder(undefined);
                    getOrders(true);
                  } catch (error) {
                    showToast({ type: "error", message: "Erro ao alterar faturamento" });
                  } finally {
                    setIsChangingBilling(false);
                  }
                }}
                isSaveLoading={isChangingBilling}
                saveText="Salvar Alterações"
                cancelText="Cancelar"
              >
                <div style={{ padding: "16px 0" }}>
                  <FloatingSelect
                    label="Método de Cobrança *"
                    value={selectedBillingMethod}
                    onChange={(e) => setSelectedBillingMethod(e.target.value)}
                  >
                    <option value="bill">Fatura</option>
                    <option value="fixcoin">FixCoins</option>
                    <option value="courtesy">Cortesia</option>
                  </FloatingSelect>
                </div>
              </FormModal>
            )}

            <ListContainer>
              {user && [roles.admin, roles.creator].includes(user.role) ? (
                <ProductionQueueSection>
                  <ProductionQueueWrapper isActive={isProductionQueueExpanded}>
                    <ProductionQueueContainer onClick={toggleProductionQueue} isActive={isProductionQueueExpanded}>
                      <ProductionQueueTitle isActive={isProductionQueueExpanded} className="queue-title">
                        {isProductionQueueExpanded ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                        A Produzir
                      </ProductionQueueTitle>
                      
                      <RightControlsWrapper isActive={isProductionQueueExpanded}>
                        <BadgeWrapper>
                          <QueueCountBadge>
                            A PRODUZIR: {sortedProductionQueueOrders.length}
                          </QueueCountBadge>
                          
                          <UrgentPriorityBadge>
                            <RiAlarmWarningFill size={10} /> 
                            <span className="badge-text">
                              URGENTES: {sortedProductionQueueOrders.filter(o => o && (o.urgency_type === orderUrgencyTypes.requested || o.urgency_type === orderUrgencyTypes.approved)).length}
                            </span>
                          </UrgentPriorityBadge>
                        </BadgeWrapper>
                      </RightControlsWrapper>
                    </ProductionQueueContainer>

                    {isProductionQueueExpanded && (
                      sortedProductionQueueOrders.length === 0 ? (
                        <EmptyQueueMessage>Nenhum pedido pendente.</EmptyQueueMessage>
                      ) : (
                        <Table 
                          columns={columns}
                          data={sortedProductionQueueOrders}
                          actions={actions}
                          rowClassName={(row) => (row.urgency_type === orderUrgencyTypes.requested || row.urgency_type === orderUrgencyTypes.approved) ? "urgency-row" : ""}
                          onRowClick={(row) => {
                            setSelectedOrderId(row.id);
                            setSeeDetails(true);
                          }}
                        />
                      )
                    )}
                  </ProductionQueueWrapper>


                  <Table 
                    columns={columns}
                    data={otherOrders}
                    actions={actions}
                    rowClassName={(row) => (row.urgency_type === orderUrgencyTypes.requested || row.urgency_type === orderUrgencyTypes.approved) ? "urgency-row" : ""}
                    onRowClick={(row) => {
                      setSelectedOrderId(row.id);
                      setSeeDetails(true);
                    }}
                  />
                </ProductionQueueSection>
              ) : (
                <Table 
                  columns={columns}
                  data={orders}
                  actions={actions}
                  rowClassName={(row) => (row.urgency_type === orderUrgencyTypes.requested || row.urgency_type === orderUrgencyTypes.approved) ? "urgency-row" : ""}
                  onRowClick={(row) => {
                    setSelectedOrderId(row.id);
                    setSeeDetails(true);
                  }}
                />
              )}
            </ListContainer>

            <Footer>
              {loading ? (
                <ReactLoading
                  type={"bubbles"}
                  color={"var(--fixit)"}
                  height={64}
                  width={64}
                />
              ) : page < totalPage ? (
                <LoadMore onClick={loadMore}>Carregar mais...</LoadMore>
              ) : (
                ""
              )}
            </Footer>
          </Container>
        </>
      }
    </>
  );
}
